import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { ManageCampaignService } from 'src/app/private/services/manage-campaign.service';
import { MessageService } from 'primeng/api';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

interface WhatsappTrackingData {
  id: string;
  campaign_id: string;
  organization_id: string;
  campus_id: string | null;
  message_id: string;
  recipient_phone: string;
  template_id: string;
  event_type: string;
  status: string;
  event_count: number;
  event_at: string;
  raw_event_data: string;
  createdAt: string;
  updatedAt: string;
}

interface WhatsappStatistics {
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  uniqueDelivered?: number;
  uniqueRead?: number;
}

interface WhatsappReportData {
  tracking_data: WhatsappTrackingData[];
  statistics: WhatsappStatistics;
}

@Component({
  selector: 'app-whatsapp-report',
  imports: [SharedModule],
  templateUrl: './whatsapp-report.component.html',
  styleUrl: './whatsapp-report.component.scss'
})
export class WhatsappReportComponent implements OnInit {
  @Input() campaignId!: string;
  @Input() campaignName!: string;
  @Output() closeDialog = new EventEmitter<void>();

  loading: boolean = false;
  reportData: WhatsappReportData | null = null;
  filteredData: WhatsappTrackingData[] = [];
  groupedData: any[] = []; // For showing unique phones with counts
  
  activeTab: string = 'all';
  activeView: string = 'table'; // 'table' or 'graph'

  // Statistics
  statistics: WhatsappStatistics = {
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    uniqueDelivered: 0,
    uniqueRead: 0
  };

  // Chart Data
  statusChartData: any;
  statusChartOptions: any;
  
  timelineChartData: any;
  timelineChartOptions: any;

  // Table columns
  displayedColumns: any[] = [
    { field: 'recipient_phone', header: 'Recipient Phone', sortable: true },
    { field: 'event_type', header: 'Event Type', sortable: true },
    { field: 'status', header: 'Status', sortable: true },
    { field: 'event_at', header: 'Event Time', sortable: true }
  ];

  // Columns for grouped view (read/delivered)
  groupedColumns: any[] = [
    { field: 'recipient_phone', header: 'Recipient Phone', sortable: true },
    { field: 'event_count', header: 'Event Count', sortable: true },
    { field: 'first_event', header: 'First Event', sortable: true },
    { field: 'last_event', header: 'Last Event', sortable: true }
  ];

  // Status tabs
  statusTabs = [
    { label: 'All', value: 'all', icon: 'pi pi-list', count: 0 },
    { label: 'Sent', value: 'sent', icon: 'pi pi-send', count: 0 },
    { label: 'Delivered', value: 'delivered', icon: 'pi pi-check-circle', count: 0 },
    { label: 'Read', value: 'read', icon: 'pi pi-eye', count: 0 },
    { label: 'Failed', value: 'failed', icon: 'pi pi-exclamation-triangle', count: 0 }
  ];

  constructor(
    private api: ManageCampaignService,
    private messageService: MessageService,
    private localStorage: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.loadReportData();
  }

  loadReportData(): void {
    this.loading = true;

    const payload = {
      campaign_id: this.campaignId,
      organization_id: this.localStorage.getOrganizationId()
    };

    this.api.getWhatsappCampaignReport(payload).subscribe({
      next: (data: WhatsappReportData) => {
        this.processReportData(data);
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Failed to load WhatsApp report data' 
        });
        this.loading = false;
        console.error('Error loading WhatsApp report:', error);
      }
    });
  }

  processReportData(data: WhatsappReportData): void {
    this.reportData = data;
    this.statistics = data.statistics;
    this.filteredData = data.tracking_data;
    
    // Calculate unique phone counts for delivered and read
    const uniqueDeliveredPhones = new Set(
      data.tracking_data
        .filter(item => item.status.toLowerCase() === 'delivered')
        .map(item => item.recipient_phone)
    );
    
    const uniqueReadPhones = new Set(
      data.tracking_data
        .filter(item => item.status.toLowerCase() === 'read')
        .map(item => item.recipient_phone)
    );
    
    this.statistics.uniqueDelivered = uniqueDeliveredPhones.size;
    this.statistics.uniqueRead = uniqueReadPhones.size;
    
    // Update tab counts
    this.statusTabs.forEach(tab => {
      if (tab.value === 'all') {
        tab.count = data.tracking_data.length;
      } else if (tab.value === 'delivered') {
        tab.count = this.statistics.uniqueDelivered || 0;
      } else if (tab.value === 'read') {
        tab.count = this.statistics.uniqueRead || 0;
      } else {
        tab.count = data.statistics[tab.value as keyof WhatsappStatistics] || 0;
      }
    });

    this.initializeCharts();
    this.filterDataByStatus(this.activeTab);
  }

  filterDataByStatus(status: string): void {
    this.activeTab = status;
    
    if (!this.reportData) return;
    
    if (status === 'all') {
      this.filteredData = this.reportData.tracking_data;
      this.groupedData = [];
    } else if (status === 'read' || status === 'delivered') {
      // For read and delivered, show unique phones with counts
      const statusData = this.reportData.tracking_data.filter(
        item => item.status.toLowerCase() === status.toLowerCase()
      );
      
      // Group by phone
      const phoneGroups = new Map<string, WhatsappTrackingData[]>();
      statusData.forEach(item => {
        if (!phoneGroups.has(item.recipient_phone)) {
          phoneGroups.set(item.recipient_phone, []);
        }
        phoneGroups.get(item.recipient_phone)!.push(item);
      });
      
      // Create grouped data
      this.groupedData = Array.from(phoneGroups.entries()).map(([phone, events]) => {
        const sortedEvents = events.sort((a, b) => 
          new Date(a.event_at).getTime() - new Date(b.event_at).getTime()
        );
        return {
          recipient_phone: phone,
          event_count: events.length,
          first_event: sortedEvents[0].event_at,
          last_event: sortedEvents[sortedEvents.length - 1].event_at,
          status: status
        };
      });
      
      this.filteredData = [];
    } else {
      this.filteredData = this.reportData.tracking_data.filter(
        item => item.status.toLowerCase() === status.toLowerCase()
      );
      this.groupedData = [];
    }
  }

  initializeCharts(): void {
    // Status Distribution Pie Chart
    this.statusChartData = {
      labels: ['Sent', 'Delivered (Unique)', 'Read (Unique)', 'Failed'],
      datasets: [{
        data: [
          this.statistics.sent,
          this.statistics.uniqueDelivered || 0,
          this.statistics.uniqueRead || 0,
          this.statistics.failed
        ],
        backgroundColor: [
          '#3B82F6',
          '#10B981',
          '#8B5CF6',
          '#EF4444'
        ],
        borderWidth: 0
      }]
    };

    this.statusChartOptions = {
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.parsed || 0;
              const total = this.statistics.sent;
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    };

    // Timeline Bar Chart
    this.timelineChartData = {
      labels: ['Sent', 'Delivered (Unique)', 'Read (Unique)', 'Failed'],
      datasets: [{
        label: 'Event Count',
        data: [
          this.statistics.sent,
          this.statistics.uniqueDelivered || 0,
          this.statistics.uniqueRead || 0,
          this.statistics.failed
        ],
        backgroundColor: '#10B981',
        borderRadius: 8
      }]
    };

    this.timelineChartOptions = {
      indexAxis: 'y',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `Count: ${context.parsed.x}`;
            }
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            stepSize: 1
          }
        }
      }
    };
  }

  switchView(view: string): void {
    this.activeView = view;
  }

  getStatusBadgeClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'sent': 'status-sent',
      'delivered': 'status-delivered',
      'read': 'status-read',
      'failed': 'status-failed'
    };
    return statusMap[status.toLowerCase()] || 'status-default';
  }

  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  isGroupedView(): boolean {
    return this.activeTab === 'read' || this.activeTab === 'delivered';
  }

  getDisplayColumns(): any[] {
    return this.isGroupedView() ? this.groupedColumns : this.displayedColumns;
  }

  getDisplayData(): any[] {
    return this.isGroupedView() ? this.groupedData : this.filteredData;
  }

  calculateDeliveryRate(): number {
    if (this.statistics.sent === 0) return 0;
    return (((this.statistics.uniqueDelivered || 0) / this.statistics.sent) * 100);
  }

  calculateReadRate(): number {
    const uniqueDelivered = this.statistics.uniqueDelivered || 0;
    if (uniqueDelivered === 0) return 0;
    return (((this.statistics.uniqueRead || 0) / uniqueDelivered) * 100);
  }

  calculateFailureRate(): number {
    if (this.statistics.sent === 0) return 0;
    return ((this.statistics.failed / this.statistics.sent) * 100);
  }

  refreshReport(): void {
    this.loadReportData();
  }

  close(): void {
    this.closeDialog.emit();
  }
}
